<script setup lang="ts">
import { ref } from 'vue'
import type { Idol } from '../game/types'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'recruit', idolId: string): void
}>()

// 模拟招募池
const candidateIdols = ref<Idol[]>([
  {
    id: 'idol_new_1',
    name: '樱井 花音',
    rarity: 'R',
    level: 1,
    exp: 0,
    stats: { vocal: 45, dance: 52, charm: 60, stamina: 50, mental: 48 },
    status: 'active',
    skills: [],
    scandal: 0,
    fans: 0,
    groupId: null,
    joinDate: { year: 1, month: 4, week: 2 },
    graduationRisk: false,
    relationships: {},
    portraitUrl: '',
    backstory: '来自大阪的元气少女，梦想成为 center。',
  },
  {
    id: 'idol_new_2',
    name: '水野 蓝子',
    rarity: 'N',
    level: 1,
    exp: 0,
    stats: { vocal: 35, dance: 40, charm: 42, stamina: 45, mental: 50 },
    status: 'active',
    skills: [],
    scandal: 0,
    fans: 0,
    groupId: null,
    joinDate: { year: 1, month: 4, week: 2 },
    graduationRisk: false,
    relationships: {},
    portraitUrl: '',
    backstory: '安静内敛，但有着意外的歌唱天赋。',
  },
  {
    id: 'idol_new_3',
    name: '天宫 瑠璃',
    rarity: 'SR',
    level: 1,
    exp: 0,
    stats: { vocal: 70, dance: 65, charm: 75, stamina: 55, mental: 60 },
    status: 'active',
    skills: [],
    scandal: 0,
    fans: 0,
    groupId: null,
    joinDate: { year: 1, month: 4, week: 2 },
    graduationRisk: false,
    relationships: {},
    portraitUrl: '',
    backstory: '前知名事务所练习生，因理念不合离开。',
  },
])

const selectedIdol = ref<Idol | null>(null)
const isRecruiting = ref(false)

const rarityColors: Record<string, string> = {
  N: 'text-rarity-N border-rarity-N',
  R: 'text-rarity-R border-rarity-R',
  SR: 'text-rarity-SR border-rarity-SR',
  SSR: 'text-accent-gold border-accent-gold',
}

function handleSelect(idol: Idol) {
  selectedIdol.value = idol
}

function handleRecruit() {
  if (!selectedIdol.value || isRecruiting.value) return
  isRecruiting.value = true
  setTimeout(() => {
    emit('recruit', selectedIdol.value!.id)
  }, 500)
}
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <!-- 背景遮罩 -->
    <div class="absolute inset-0 bg-bg-deep bg-opacity-80" @click="emit('close')"></div>

    <!-- 面板 -->
    <div class="relative w-full max-w-2xl bg-bg-surface rounded-lg border border-accent-purple border-opacity-20 shadow-modal overflow-hidden">
      <!-- 头部 -->
      <div class="flex items-center justify-between p-4 border-b border-accent-purple border-opacity-10">
        <h2 class="text-lg font-display font-bold text-text-primary">招募试镜</h2>
        <button @click="emit('close')" class="text-text-muted hover:text-text-secondary text-xl">×</button>
      </div>

      <!-- 候选人 -->
      <div class="p-4">
        <p class="text-text-muted text-sm mb-4">本次试镜共有 {{ candidateIdols.length }} 名候选人，选择一位招募：</p>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div
            v-for="idol in candidateIdols"
            :key="idol.id"
            @click="handleSelect(idol)"
            class="p-4 rounded-lg border cursor-pointer transition-all"
            :class="[
              selectedIdol?.id === idol.id
                ? 'bg-primary bg-opacity-10 border-primary'
                : 'bg-bg-base border-bg-elevated hover:border-accent-purple'
            ]"
          >
            <!-- 头像 -->
            <div class="w-16 h-16 mx-auto mb-3 rounded-lg bg-gradient-to-br from-bg-surface to-bg-elevated flex items-center justify-center text-2xl">
              {{ idol.name.charAt(0) }}
            </div>

            <!-- 信息 -->
            <div class="text-center">
              <p class="text-text-primary font-medium mb-1">{{ idol.name }}</p>
              <span
                class="inline-block px-2 py-0.5 border rounded text-xs font-bold"
                :class="rarityColors[idol.rarity]"
              >{{ idol.rarity }}</span>
            </div>

            <!-- 属性概览 -->
            <div class="mt-3 grid grid-cols-2 gap-1 text-xs">
              <div class="text-text-muted">歌唱 <span class="text-text-secondary">{{ idol.stats.vocal }}</span></div>
              <div class="text-text-muted">舞蹈 <span class="text-text-secondary">{{ idol.stats.dance }}</span></div>
              <div class="text-text-muted">魅力 <span class="text-text-secondary">{{ idol.stats.charm }}</span></div>
              <div class="text-text-muted">心理 <span class="text-text-secondary">{{ idol.stats.mental }}</span></div>
            </div>
          </div>
        </div>

        <!-- 选中详情 -->
        <div v-if="selectedIdol" class="bg-bg-base rounded-lg p-4 mb-4">
          <p class="text-text-muted text-sm mb-2">{{ selectedIdol.backstory }}</p>
          <div class="flex gap-4 text-xs text-text-secondary">
            <span>招募费用: <span class="text-accent-gold">¥30,000</span></span>
            <span>招募后进入 2 周适应期</span>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex gap-3">
          <button
            @click="handleRecruit"
            :disabled="!selectedIdol || isRecruiting"
            class="flex-1 py-3 bg-primary text-bg-deep font-medium rounded-md hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {{ isRecruiting ? '招募中...' : '确认招募' }}
          </button>
          <button
            @click="emit('close')"
            class="px-6 py-3 border border-bg-elevated text-text-muted rounded-md hover:border-accent-purple transition-colors"
          >
            取消
          </button>
        </div>
      </div>

      <!-- 底部提示 -->
      <div class="px-4 py-3 bg-bg-base border-t border-accent-purple border-opacity-10">
        <p class="text-text-disabled text-xs text-center">💡 消耗 1 AP 进行试镜，候选人每季度刷新</p>
      </div>
    </div>
  </div>
</template>
